/**
 * Funções puras de cálculo de horário para o agendador.
 *
 * Os horários são sempre INTERPRETADOS NO FUSO LOCAL do usuário. Persistimos
 * apenas `{ tipo, hora, minuto, diasSemana?, diaMes? }` — nunca epoch — para
 * que mudanças de DST e troca de fuso da máquina sejam transparentes.
 */

export const TIPOS_FREQUENCIA = ["diario", "semanal", "mensal"];

/**
 * Quantos dias o mês tem (1-12 → 28..31). Janeiro = 1.
 */
function diasNoMes(ano, mes) {
  // Date(ano, mes, 0) retorna o último dia do mês anterior — truque clássico.
  return new Date(ano, mes, 0).getDate();
}

/**
 * Garante que `diaMes` seja válido para o mês/ano. Se passar (ex.: dia 31
 * em fevereiro), recua para o último dia do mês.
 */
export function clampDiaMes(diaMes, mes, ano) {
  const max = diasNoMes(ano, mes);
  return Math.min(Math.max(1, diaMes | 0), max);
}

function clonarData(d) {
  return new Date(d.getTime());
}

function setHoraMinuto(data, hora, minuto) {
  const d = clonarData(data);
  d.setHours(hora, minuto, 0, 0);
  return d;
}

/**
 * Calcula a próxima execução em diário.
 */
function proximaDiaria({ hora, minuto }, agora) {
  let alvo = setHoraMinuto(agora, hora, minuto);
  if (alvo.getTime() <= agora.getTime()) {
    alvo.setDate(alvo.getDate() + 1);
  }
  return alvo;
}

/**
 * Calcula a próxima execução em semanal.
 * @param {number[]} diasSemana 0=domingo .. 6=sábado
 */
function proximaSemanal({ diasSemana = [], hora, minuto }, agora) {
  if (!diasSemana.length) return null;
  const ordenados = [...diasSemana].map((d) => d % 7).sort((a, b) => a - b);

  for (let inc = 0; inc < 14; inc++) {
    const candidato = clonarData(agora);
    candidato.setDate(candidato.getDate() + inc);
    const dow = candidato.getDay();
    if (!ordenados.includes(dow)) continue;
    const horarioAlvo = setHoraMinuto(candidato, hora, minuto);
    if (horarioAlvo.getTime() > agora.getTime()) {
      return horarioAlvo;
    }
  }
  return null;
}

/**
 * Calcula a próxima execução em mensal.
 * Se `diaMes` for maior que dias do mês corrente, usa o último dia.
 */
function proximaMensal({ diaMes, hora, minuto }, agora) {
  let ano = agora.getFullYear();
  let mes = agora.getMonth() + 1;
  for (let i = 0; i < 24; i++) {
    const diaEfetivo = clampDiaMes(diaMes, mes, ano);
    const candidato = new Date(ano, mes - 1, diaEfetivo, hora, minuto, 0, 0);
    if (candidato.getTime() > agora.getTime()) return candidato;
    mes++;
    if (mes > 12) {
      mes = 1;
      ano++;
    }
  }
  return null;
}

/**
 * Calcula a próxima execução de um agendamento, a partir de "agora".
 *
 * @param {Object} frequencia { tipo, hora, minuto, diasSemana?, diaMes? }
 * @param {Date} [agora=new Date()]
 * @returns {Date|null}
 */
export function proximaExecucao(frequencia, agora = new Date()) {
  if (!frequencia || typeof frequencia !== "object") return null;
  const hora = frequencia.hora ?? 0;
  const minuto = frequencia.minuto ?? 0;
  switch (frequencia.tipo) {
    case "diario":
      return proximaDiaria({ hora, minuto }, agora);
    case "semanal":
      return proximaSemanal({ diasSemana: frequencia.diasSemana, hora, minuto }, agora);
    case "mensal":
      return proximaMensal({ diaMes: frequencia.diaMes, hora, minuto }, agora);
    default:
      return null;
  }
}

/**
 * Dado um agendamento ativo com `ultimaExecucao`, retorna se há execução
 * atrasada (entre ultimaExecucao e agora). Retorna apenas TRUE/FALSE — o
 * agendador deduplica execuções atrasadas (só executa a mais recente
 * "vencida", não todas as ocorrências que poderiam ter rodado).
 */
export function temAtrasado(frequencia, ultimaExecucao, agora = new Date()) {
  if (!frequencia) return false;
  if (!ultimaExecucao) {
    // Sem registro de execução anterior: considera atrasado se a hora alvo
    // do dia já passou (para diário) ou de algum dia desde ontem.
    const proxima = proximaExecucao(frequencia, new Date(agora.getTime() - 24 * 3600 * 1000));
    return proxima ? proxima.getTime() <= agora.getTime() : false;
  }
  const ult = new Date(ultimaExecucao);
  if (Number.isNaN(ult.getTime())) return false;
  const proxima = proximaExecucao(frequencia, ult);
  return proxima ? proxima.getTime() <= agora.getTime() : false;
}

export const DIAS_SEMANA = [
  { value: 0, label: "Domingo", abrev: "Dom" },
  { value: 1, label: "Segunda", abrev: "Seg" },
  { value: 2, label: "Terça", abrev: "Ter" },
  { value: 3, label: "Quarta", abrev: "Qua" },
  { value: 4, label: "Quinta", abrev: "Qui" },
  { value: 5, label: "Sexta", abrev: "Sex" },
  { value: 6, label: "Sábado", abrev: "Sáb" },
];

export function formatarFrequencia(frequencia) {
  if (!frequencia) return "";
  const hh = String(frequencia.hora ?? 0).padStart(2, "0");
  const mm = String(frequencia.minuto ?? 0).padStart(2, "0");
  switch (frequencia.tipo) {
    case "diario":
      return `Diário às ${hh}:${mm}`;
    case "semanal": {
      const dias = (frequencia.diasSemana || [])
        .map((d) => DIAS_SEMANA.find((x) => x.value === d)?.abrev || "")
        .filter(Boolean)
        .join(", ");
      return `Semanal (${dias}) às ${hh}:${mm}`;
    }
    case "mensal":
      return `Mensal no dia ${frequencia.diaMes} às ${hh}:${mm}`;
    default:
      return "";
  }
}
